import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";

import { useEmpresa } from "../../../app/providers/EmpresaProvider";
import TreasuryApi from "./treasuryApi";
import { DEFAULT_MOVEMENT_OPTIONS } from "./treasuryOptions";
import {
  renderSelectOptions,
  StyledTreasuryField,
  TreasuryPanelFrame,
} from "./TreasuryShared";

const FALLBACK_DEFAULT_MOVEMENT_TYPE = "INCOME";

function normalizeDefaultMovementType(value) {
  return DEFAULT_MOVEMENT_OPTIONS.some((option) => option.value === value)
    ? value
    : FALLBACK_DEFAULT_MOVEMENT_TYPE;
}

export default function TreasuryGeneralSettingsPanel() {
  const { empresaId } = useEmpresa();
  const licenseId = empresaId || "1";

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [defaultMovementType, setDefaultMovementType] = useState(
    FALLBACK_DEFAULT_MOVEMENT_TYPE
  );
  const [savedMovementType, setSavedMovementType] = useState(
    FALLBACK_DEFAULT_MOVEMENT_TYPE
  );
  const [loadError, setLoadError] = useState("");
  const [toast, setToast] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  const hasChanges = useMemo(
    () => defaultMovementType !== savedMovementType,
    [defaultMovementType, savedMovementType]
  );

  const handleCloseToast = () => setToast((prev) => ({ ...prev, open: false }));

  const loadSettings = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const data = await TreasuryApi.getSettings({ licenseId });
      const nextValue = normalizeDefaultMovementType(data?.default_movement_type);
      setDefaultMovementType(nextValue);
      setSavedMovementType(nextValue);
    } catch (error) {
      console.error("treasury settings", error);
      setDefaultMovementType(FALLBACK_DEFAULT_MOVEMENT_TYPE);
      setSavedMovementType(FALLBACK_DEFAULT_MOVEMENT_TYPE);
      setLoadError(
        error?.message || "No se pudo cargar la configuración de tesorería."
      );
    } finally {
      setLoading(false);
    }
  }, [licenseId]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const data = await TreasuryApi.updateSettings(
        { default_movement_type: defaultMovementType },
        { licenseId }
      );
      const nextValue = data?.default_movement_type
        ? normalizeDefaultMovementType(data.default_movement_type)
        : defaultMovementType;
      setDefaultMovementType(nextValue);
      setSavedMovementType(nextValue);
      setToast({
        open: true,
        severity: "success",
        message: "Configuración guardada correctamente.",
      });
    } catch (error) {
      setToast({
        open: true,
        severity: "error",
        message: error?.message || "No se pudo guardar la configuración.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <TreasuryPanelFrame
        title="Configuración de tesorería"
        description="Definí el tipo de movimiento predeterminado para nuevas filas."
        onRefresh={loadSettings}
        refreshing={loading}
        saving={saving}
      >
        <Box
          sx={{
            p: { xs: 2, md: 2.5 },
            borderRadius: "24px",
            bgcolor: "neutral.50",
            boxShadow: "0 14px 36px rgba(15, 23, 42, 0.08)",
            minHeight: 180,
          }}
        >
          {loading ? (
            <Stack alignItems="center" justifyContent="center" sx={{ py: 5 }}>
              <CircularProgress size={28} />
            </Stack>
          ) : (
            <Box component="form" onSubmit={handleSubmit}>
              {loadError ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {loadError}
                </Alert>
              ) : null}

              <Typography sx={{ fontSize: 18, fontWeight: 900, color: "primary.900", mb: 2 }}>
                Tipo de movimiento predeterminado
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gap: 1.5,
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "minmax(0, 360px)",
                  },
                }}
              >
                <StyledTreasuryField
                  select
                  label="Tipo predeterminado"
                  name="default_movement_type"
                  value={defaultMovementType}
                  onChange={(event) => setDefaultMovementType(event.target.value)}
                  required
                >
                  {renderSelectOptions(DEFAULT_MOVEMENT_OPTIONS)}
                </StyledTreasuryField>
              </Box>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                justifyContent="flex-end"
                sx={{ mt: 2 }}
              >
                <Button
                  type="button"
                  color="secondary"
                  variant="outlined"
                  disabled={saving || !hasChanges}
                  onClick={() => setDefaultMovementType(savedMovementType)}
                  sx={{ borderRadius: "33px", px: 3, py: 1, textTransform: "none", fontWeight: 700 }}
                >
                  Cancelar
                </Button>

                <Button
                  type="submit"
                  variant="contained"
                  disabled={saving || !hasChanges}
                  startIcon={<SaveIcon />}
                  sx={{ borderRadius: "33px", px: 3, py: 1, textTransform: "none", fontWeight: 700 }}
                >
                  {saving ? "Guardando..." : "Guardar configuración"}
                </Button>
              </Stack>
            </Box>
          )}
        </Box>
      </TreasuryPanelFrame>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={handleCloseToast} severity={toast.severity} variant="filled" sx={{ width: "100%" }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </>
  );
}
